import type { RouteMaster } from '../../../data/routes';

/**
 * 方面ブロック内の「遅延を判定する向き」表 (`ConnectingDirectionEntry.route_directions`)。
 *
 * ## 何のための項目か
 *
 * 接続線ブロックで「遅延を検索する線区」を指定したとき、
 * **線区によって「同じ向き」を表す `direction_id` が違う** ことがある。
 *
 * | route_id                      | direction_id | 方面名 |
 * | ----------------------------- | ------------ | ------ |
 * | 高崎線                         | `"0"`        | 下り   |
 * | 上野東京ライン-高崎系統         | `"1"`        | 北行   |
 * | 湘南新宿ライン-高崎東海道系統   | `"1"`        | 北行   |
 *
 * そのため判定する向きは **線区ごと** に選ぶ (線区が 1 件でも同じ表で選ぶ)。
 * 以前は方面ブロック共通の「基本の向き」(`direction_id`) と、その例外としての
 * 線区ごとの上書きに分かれていたが、運用者には違いが分かりにくいため表に一本化した。
 *
 * ## 解決順 (放送側 `_resolve_route_direction_id` と一致させること)
 *
 * 1. `route_directions[route_id]` に非空値があればそれを使う
 * 2. 無ければ方面ブロック共通の `direction_id` を使う (旧データ互換)
 *
 * ## 旧データ (`direction_id` のみ設定済み) の扱い
 *
 * 画面を開いただけでは書き換えない (放送 SSML が 1 バイトも変わらないようにするため)。
 * 各行には `direction_id` を適用した場合の向きを初期値として表示する。
 * いずれかの行を変更した時点で、全線区の実効値を `route_directions` へ展開し、
 * `direction_id` は取り除く。展開しないと、画面に出ない `direction_id` が
 * 「(指定なし)」の行へ効き続けてしまうため。
 * `"0"` / `"1"` 以外の `direction_id` は保存 API が `route_directions` で受け付けないので
 * 展開せず捨てる (放送側の /stop-times も 0/1 しか受け付けず、そもそも有効に働かない値)。
 *
 * `whole_line` モードは方面を判定に使わないため、呼び出し側で非表示にする。
 */
export function RouteDirectionOverridesTable({
  testIdPrefix,
  ariaLabelPrefix,
  routeIds,
  routes,
  value,
  legacyDirectionId,
  onChange,
}: {
  /** `data-testid` の接頭辞 (例: `connecting-direction-route-directions-0-0`)。 */
  testIdPrefix: string;
  /** `aria-label` の接頭辞 (例: `接続線#1 方面#1 `)。 */
  ariaLabelPrefix?: string;
  /**
   * 対象の線区一覧 (遅延を検索する線区)。この並び順で行を出す
   * (遅延取得順・読み上げ順と一致させ、運用者が対応を追いやすくする)。
   */
  routeIds: readonly string[];
  /** 方面選択肢の解決に使う ATOS 線区マスタ。取得失敗時は空配列でよい。 */
  routes: readonly RouteMaster[];
  /** 現在の設定 (route_id → direction_id)。 */
  value: Readonly<Record<string, string>> | undefined;
  /**
   * 旧データの方面ブロック共通 `direction_id`。
   * 行の初期表示と、変更時の `route_directions` への展開に使う。
   */
  legacyDirectionId?: string;
  /**
   * 設定が変わったときに呼ぶ。`directionId` は常に取り除く (undefined) ため、
   * 呼び出し側は `route_directions` と一緒に `direction_id` も更新すること。
   */
  onChange: (next: {
    routeDirections: Record<string, string> | undefined;
    directionId: undefined;
  }) => void;
}): React.JSX.Element {
  // 型は string[] だが、既存データ / import された JSON が壊れている可能性に備えて
  // 実行時にも配列と文字列を確認する (画面が白くなるのを防ぐ)。
  const targetRouteIds = (Array.isArray(routeIds) ? routeIds : []).filter(
    (routeId): routeId is string => typeof routeId === 'string' && routeId !== '',
  );

  const overrides: Record<string, string> =
    value && typeof value === 'object' && !Array.isArray(value) ? { ...value } : {};
  const legacy = typeof legacyDirectionId === 'string' ? legacyDirectionId.trim() : '';

  /** 線区の方面選択肢 (`routes-master.json` の `directions`) を解決する。 */
  const directionOptionsOf = (routeId: string): { value: string; label: string }[] => {
    const master = routes.find((r) => r.route_id === routeId);
    return (master?.directions ?? []).map((d, i) => ({
      value:
        typeof d.direction_id === 'string' && d.direction_id !== '' ? d.direction_id : String(i),
      label: d.direction_name,
    }));
  };

  /** 行に表示する値。線区ごとの設定 → 旧データの共通値 → 空 (指定なし) の順。 */
  const currentOf = (routeId: string): string => {
    const own = overrides[routeId];
    if (typeof own === 'string' && own !== '') return own;
    return legacy;
  };

  /**
   * 1 行ぶんを更新する。空文字 (= 指定なし) はキーごと削除し、全行が空なら
   * `undefined` を emit して DB / JSON にゴミを残さない
   * (サーバ側も空オブジェクトはキーごと保存しない = 既存 item と bit-perfect 互換)。
   *
   * 旧データの共通 `direction_id` は、ここで他の行へ展開してから取り除く。
   */
  const emit = (routeId: string, directionId: string): void => {
    const next = { ...overrides };
    if (legacy === '0' || legacy === '1') {
      for (const id of targetRouteIds) {
        const own = next[id];
        if (typeof own !== 'string' || own === '') next[id] = legacy;
      }
    }
    if (directionId === '') {
      delete next[routeId];
    } else {
      next[routeId] = directionId;
    }
    onChange({
      routeDirections: Object.keys(next).length > 0 ? next : undefined,
      directionId: undefined,
    });
  };

  return (
    <div className="route-direction-overrides" data-testid={testIdPrefix}>
      {targetRouteIds.length === 0 ? (
        <div className="connecting-section__empty" data-testid={`${testIdPrefix}-empty`}>
          先に「遅延を検索する線区」を選んでください。
        </div>
      ) : (
        <>
          <p className="connecting-section__lead">
            線区ごとに、遅延を判定する向きを選びます。指定しない線区は向きを絞らずに判定します。
          </p>
          <table className="route-direction-overrides__table">
            <thead>
              <tr>
                <th>線区</th>
                <th>判定する向き</th>
              </tr>
            </thead>
            <tbody>
              {targetRouteIds.map((routeId, i) => {
                const options = directionOptionsOf(routeId);
                const current = currentOf(routeId);
                // マスタから消えた / 別体系の値が既存データに入っていても画面から消さない
                // (ConnectingRouteIdsSelect の「一覧にない線区」と同じ扱い)。
                const isUnknownDirection =
                  current !== '' && !options.some((o) => o.value === current);
                const master = routes.find((r) => r.route_id === routeId);
                // 「遅延を検索する線区」(ConnectingRouteIdsSelect) と同じく、route_id は内部値なので
                // 画面には出さず、マスタの線名 → route_id の順で表示する。
                const routeName = master?.route_name || routeId;
                return (
                  <tr key={`${routeId}-${i}`} data-testid={`${testIdPrefix}-row-${i}`}>
                    <td className="route-direction-overrides__route">{routeName}</td>
                    <td>
                      <select
                        aria-label={`${ariaLabelPrefix ?? ''}${routeName} の判定用方面`}
                        data-testid={`${testIdPrefix}-select-${i}`}
                        value={current}
                        onChange={(e) => emit(routeId, e.target.value)}
                      >
                        <option value="">(指定なし)</option>
                        {options.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                        {isUnknownDirection ? (
                          <option value={current}>{current} (一覧にない方面)</option>
                        ) : null}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
      <details className="connecting-section__details">
        <summary>詳しい説明</summary>
        <p className="hint">
          例）大宮で「宮原・上尾方面」を案内する場合、高崎線は <strong>下り</strong>
          、上野東京ライン / 湘南新宿ラインは <strong>北行</strong>{' '}
          のように、線区によって向きの呼び方が違います。
        </p>
        <p className="hint">
          放送文は、上の <strong>放送文で読み上げる方面名</strong> 1
          つにまとめて読み上げます。向きは遅延の判定にだけ使われます。
        </p>
      </details>
    </div>
  );
}
